/**
 * Phone Prefix Helpers
 * Ported from breitling-storefront-next/src/lib/phonePrefixes/helpers.ts
 */

import { COUNTRY_CALLING_CODES } from './const';
import type { CountryCode, PhonePrefixCountry } from './types';

/**
 * Phone prefix options keyed by ISO country code.
 * Uses static labels so SSR and client markup stay identical (Intl.DisplayNames
 * can differ between Node and browser ICU data).
 */
export const getPhonePrefixCodeSelectOptions = () =>
  COUNTRY_CALLING_CODES.slice()
    .sort((a, b) => a.label.localeCompare(b.label, 'en'))
    .map((entry) => ({
      value: entry.code,
      label: `${entry.label} ${entry.value}`,
    }));

/**
 * Get phone prefix select options sorted alphabetically by country name
 * Returns options formatted for select dropdowns
 */
export const getPhonePrefixSelectOpts = () => {
  return COUNTRY_CALLING_CODES.slice()
    .sort((a, b) => a.label.localeCompare(b.label))
    .map((phonePrefix) => ({
      ...phonePrefix,
      label: `${phonePrefix.label} (${phonePrefix.value})`,
    }));
};

/**
 * Same options as getPhonePrefixSelectOpts but with `value` set to the ISO code,
 * so each <option> has a unique value (countries that share a calling code, like
 * US/CA on +1, would otherwise collide on the native <select> rendering).
 */
export const getPhonePrefixSelectOptsByCode = () =>
  getPhonePrefixSelectOpts().map((opt) => ({ ...opt, value: opt.code }));

/**
 * Get phone prefix code by value
 */
export const getPhonePrefixCode = (phonePrefixValue: string): string | undefined => {
  return COUNTRY_CALLING_CODES.find((phonePrefix) => phonePrefixValue === phonePrefix.value)?.code;
};

/**
 * Get phone prefix select option by country code
 */
export const getPhonePrefixSelectOptByCode = (
  code: CountryCode
): PhonePrefixCountry & { label: string } => {
  const opts = getPhonePrefixSelectOpts();
  return opts.find((item) => code && item.code.toUpperCase() === code.toUpperCase()) ?? opts[0];
};

/**
 * Normalize phone prefix - ensures it starts with +
 */
export const handlePhonePrefix = (phonePrefix: string | undefined): string => {
  if (!phonePrefix) return '';
  const prefix = phonePrefix.trim();
  if (!prefix.startsWith('+')) {
    return '+' + prefix;
  }
  const idx = prefix.lastIndexOf('+');
  if (idx !== 0) {
    return prefix.slice(idx);
  } else {
    return prefix;
  }
};

/**
 * Get country by phone prefix value
 */
export const getCountryByPhonePrefix = (
  phonePrefixValue: string
): PhonePrefixCountry | undefined => {
  return COUNTRY_CALLING_CODES.find(
    (country) => country.value === handlePhonePrefix(phonePrefixValue)
  );
};
